/**
 * Maps between Resource Selection UI state and the parity API
 * (CarrierSelectionConfigRequest / CarrierSelectionConfigResponse).
 */

import { DEFAULT_ALLOCATION_WEIGHTS, KPI_LIBRARY, STRATEGY_LIBRARY } from "./strategyConstants";

/** Ranked strategy + weighted allocation is the only supported parity mode. */
export const PARITY_ALLOCATION_MODE = "WEIGHTED";

/**
 * Normalizes UI `allocationWeights` into API rows. Each row carries snake + camel pct keys so Jackson
 * maps correctly whether the DTO uses `weightPct` or `weight_pct`. Never returns an empty array.
 */
export function buildAllocationWeightsPayload(strategy) {
  const raw =
    Array.isArray(strategy?.allocationWeights) && strategy.allocationWeights.length
      ? strategy.allocationWeights
      : DEFAULT_ALLOCATION_WEIGHTS.map((w) => ({ ...w }));
  const rows = raw.map((w, i) => {
    const rank = Number(w.rank ?? i + 1);
    const pct = Math.max(0, Math.min(100, Math.round(Number(w.weightPct ?? w.weight_pct ?? 0))));
    return { rank, weight_pct: pct, weightPct: pct };
  });
  const sum = rows.reduce((s, r) => s + r.weight_pct, 0);
  if (sum === 0 && rows.length) {
    return DEFAULT_ALLOCATION_WEIGHTS.map((w, idx) => {
      const pct = Math.round(Number(w.weightPct ?? 0));
      const rank = Number(w.rank ?? idx + 1);
      return { rank, weight_pct: pct, weightPct: pct };
    });
  }
  return rows;
}

const ALLOCATION_RANK_PALETTE = ["#3b82f6", "#22c55e", "#a855f7", "#f97316", "#06b6d4"];

function parseAllocationWeightsJsonBlob(maybeJson) {
  if (maybeJson == null || maybeJson === "") return null;
  if (typeof maybeJson !== "string") return null;
  try {
    const parsed = JSON.parse(maybeJson);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** Pull allocation arrays from varied API shapes (including JSON string columns). */
function rawAllocationWeightsArrayFrom(source) {
  if (!source || typeof source !== "object") return null;
  const direct =
    source.allocation_weights ??
    source.allocationWeights ??
    source.tender_allocation_weights ??
    source.tenderAllocationWeights ??
    source.rank_allocation_weights ??
    source.rankAllocationWeights;
  if (Array.isArray(direct) && direct.length) return direct;
  const j =
    source.allocation_weights_json ??
    source.allocationWeightsJson ??
    source.allocation_weight_json ??
    source.allocationWeightJson;
  return parseAllocationWeightsJsonBlob(j);
}

export function allocationWeightsFromStrategyResponse(sr) {
  const raw = rawAllocationWeightsArrayFrom(sr);
  if (Array.isArray(raw) && raw.length) {
    return raw.map((w, i) => ({
      rank: Number(w.rank ?? i + 1),
      weightPct: Math.max(0, Math.min(100, Number(w.weight_pct ?? w.weightPct ?? w.percent ?? 0))),
      color: (w.color && String(w.color)) || ALLOCATION_RANK_PALETTE[i % ALLOCATION_RANK_PALETTE.length],
    }));
  }
  return STRATEGY_LIBRARY[0].allocationWeights.map((w) => ({ ...w }));
}

function looksLikeStrategyTypeMetadataRow(obj) {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return false;
  return Boolean(
    obj.strategy_metadata_id ??
      obj.strategyMetadataId ??
      obj.strategy_type_metadata_id ??
      obj.strategyTypeMetadataId ??
      obj.metadata_id ??
      obj.metadataId ??
      obj.strategy_assignment_basis ??
      obj.strategyAssignmentBasis ??
      obj.strategy_type ??
      obj.strategyType ??
      obj.assignment_basis ??
      obj.assignmentBasis ??
      obj.basis
  );
}

function scoreRowAsConfigTypeMetadata(row) {
  if (!row || typeof row !== "object" || Array.isArray(row)) return 0;
  let s = 0;
  if (looksLikeStrategyTypeMetadataRow(row)) s += 8;
  const basis = (row.strategy_assignment_basis || row.strategyAssignmentBasis || row.basis || "")
    .toString()
    .trim()
    .toUpperCase();
  if (basis) s += 2;
  if (/^(COST|RANK|DISTANCE|TIME|TENDER)$/i.test(basis)) s += 3;
  const id =
    row.strategy_metadata_id ??
    row.strategyMetadataId ??
    row.strategy_type_metadata_id ??
    row.metadata_id ??
    row.metadataId ??
    row.id;
  if (id != null && String(id).trim().length >= 8) s += 1;
  return s;
}

function discoverMetadataArrayByWalk(node, depth, best) {
  if (node == null || depth > 16) return best;
  if (Array.isArray(node)) {
    if (node.length && node[0] != null && typeof node[0] === "object" && !Array.isArray(node[0])) {
      const score = node.reduce((acc, r) => acc + scoreRowAsConfigTypeMetadata(r), 0);
      if (score > best.score) return { score, arr: node };
    }
    return best;
  }
  if (typeof node !== "object") return best;
  let next = best;
  for (const k of Object.keys(node)) {
    next = discoverMetadataArrayByWalk(node[k], depth + 1, next);
  }
  return next;
}

/**
 * Parses `/api/resource-selection/config-type-metadata` list responses.
 * Uses paging helpers first, then common API wrappers, then a scored walk of the JSON tree.
 */
export function extractStrategyMetadataItemsFromResponse(raw) {
  if (raw == null) return [];
  const primary = extractPagedItemsFromResponse(raw);
  if (primary.length) {
    if (primary.some(looksLikeStrategyTypeMetadataRow)) return primary;
    const sum = primary.reduce((a, r) => a + scoreRowAsConfigTypeMetadata(r), 0);
    const avg = sum / primary.length;
    if (sum >= 3 || avg >= 1.5) return primary;
  }
  const unwrapCandidates = [
    raw?.data,
    raw?.result,
    raw?.payload,
    raw?.response,
    raw?.body,
    typeof raw?.success === "boolean" ? raw?.data : null,
  ].filter(Boolean);
  for (const u of unwrapCandidates) {
    if (u === raw) continue;
    const inner = extractStrategyMetadataItemsFromResponse(u);
    if (inner.length) return inner;
  }
  const embedded = raw?._embedded;
  if (embedded && typeof embedded === "object") {
    for (const v of Object.values(embedded)) {
      if (Array.isArray(v) && v.length) {
        const inner = extractStrategyMetadataItemsFromResponse(v);
        if (inner.length) return inner;
      }
    }
  }
  const flatArrays = [
    raw?.elements,
    raw?.list,
    raw?.rows,
    Array.isArray(raw?.value) ? raw.value : null,
    raw?.["hydra:member"],
  ].filter((x) => Array.isArray(x) && x.length && x[0] && typeof x[0] === "object");
  for (const arr of flatArrays) {
    const sum = arr.reduce((a, r) => a + scoreRowAsConfigTypeMetadata(r), 0);
    if (sum >= 3) return arr;
  }
  const best = discoverMetadataArrayByWalk(raw, 0, { score: 0, arr: [] });
  if (best.score >= 3 && best.arr.length) return best.arr;
  return primary.length ? primary : [];
}

/** Normalize id fields so resolveMetadataIdForBasis / findMetadataById stay consistent. */
export function normalizeStrategyMetadataRow(row) {
  if (!row || typeof row !== "object") return row;
  const id =
    row.strategy_metadata_id ??
    row.strategyMetadataId ??
    row.strategy_type_metadata_id ??
    row.strategyTypeMetadataId ??
    row.metadata_id ??
    row.metadataId ??
    row.id;
  if (id == null) return row;
  const sid = String(id);
  return {
    ...row,
    strategy_metadata_id: row.strategy_metadata_id ?? row.strategyMetadataId ?? sid,
    strategyMetadataId: row.strategyMetadataId ?? row.strategy_metadata_id ?? sid,
  };
}

function looksLikeCarrierSelectionListRow(obj) {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return false;
  return Boolean(
    obj.carrier_selection_config_id ??
      obj.carrierSelectionConfigId ??
      obj.configuration_name ??
      obj.configurationName
  );
}

function isLikelyPagedRowArray(arr) {
  if (!Array.isArray(arr) || !arr.length) return false;
  const first = arr[0];
  return first != null && typeof first === "object" && !Array.isArray(first);
}

/**
 * Spring Page uses `content`; some services use `items` / `results`.
 * Also unwraps named arrays (`config_type_metadata`, etc.) and shallow-scans nested objects.
 */
export function extractPagedItemsFromResponse(payload, depth = 0) {
  if (payload == null || depth > 10) return [];
  if (Array.isArray(payload)) {
    return isLikelyPagedRowArray(payload) ? payload : [];
  }
  if (typeof payload !== "object") return [];
  const direct =
    payload.items ??
    payload.content ??
    payload.results ??
    payload.records;
  if (Array.isArray(direct) && direct.length) return direct;
  const named =
    payload.strategy_type_metadata ??
    payload.strategyTypeMetadata ??
    payload.config_type_metadata ??
    payload.configTypeMetadata ??
    payload.metadata ??
    payload.values;
  if (Array.isArray(named) && named.length) return named;
  const dataField = payload.data;
  if (Array.isArray(dataField) && dataField.length) {
    const fromData = extractPagedItemsFromResponse(dataField, depth + 1);
    if (fromData.length) return fromData;
  }
  if (dataField && typeof dataField === "object" && !Array.isArray(dataField)) {
    const fromNestedData = extractPagedItemsFromResponse(dataField, depth + 1);
    if (fromNestedData.length) return fromNestedData;
  }
  for (const v of Object.values(payload)) {
    if (Array.isArray(v) && v.length && v.some((el) => looksLikeStrategyTypeMetadataRow(el))) {
      return v;
    }
  }
  for (const v of Object.values(payload)) {
    if (Array.isArray(v) && v.length && v.some((el) => looksLikeCarrierSelectionListRow(el))) {
      return v;
    }
  }
  for (const v of Object.values(payload)) {
    if (v && typeof v === "object" && !Array.isArray(v)) {
      const inner = extractPagedItemsFromResponse(v, depth + 1);
      if (inner.length) return inner;
    }
  }
  return [];
}

export function totalPagesFromPagedPayload(payload, pageSize, fallbackCount) {
  if (!payload || typeof payload !== "object") return 1;
  const tp = payload.total_pages ?? payload.totalPages;
  if (Number.isFinite(Number(tp)) && Number(tp) > 0) return Math.ceil(Number(tp));
  const te = payload.total_elements ?? payload.totalElements ?? payload.total;
  if (Number.isFinite(Number(te)) && Number(pageSize) > 0) {
    return Math.max(1, Math.ceil(Number(te) / Number(pageSize)));
  }
  return fallbackCount > 0 ? 1 : 1;
}

/** Merge nested DTO when list/detail wraps the config record. */
export function normalizeCarrierConfigRecord(raw) {
  if (!raw || typeof raw !== "object") return {};
  const nested = raw.carrier_selection_config ?? raw.carrierSelectionConfig;
  if (nested && typeof nested === "object") return { ...raw, ...nested };
  return raw;
}

/** Default KPI weights when a strategy has no `kpis` array. */
export const DEFAULT_KPI_WEIGHTS = Object.freeze(
  STRATEGY_LIBRARY[0].kpis.map((k) => ({ name: k.name, percent: k.percent }))
);

/** Normalize a KPI name into a stable snake_case key for `scoring_spec.weights`. */
export function scoringSpecKpiKey(name) {
  return String(name || "")
    .trim()
    .toLowerCase()
    .replace(/[^\w]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

/** Build `scoring_spec` from KPI sliders (percent 0–100 → weight 0–1). Uses `scoringWeightKey` when set (catalog variants). */
export function buildKpiScoringSpec(kpis) {
  const source = Array.isArray(kpis) ? kpis : [];
  const weights = {};
  for (const kpi of source) {
    const key = kpi?.scoringWeightKey || scoringSpecKpiKey(kpi?.name);
    if (!key) continue;
    const pct = Number(kpi?.percent ?? 0);
    weights[key] = Number.isFinite(pct) ? pct / 100 : 0;
  }
  return { weights };
}

function scoringSpecWeightKeyCount(spec) {
  if (!spec?.weights || typeof spec.weights !== "object" || Array.isArray(spec.weights)) {
    return 0;
  }
  return Object.keys(spec.weights).length;
}

/** Normalize scoring spec payloads from objects, JSON strings, or bare weight maps. */
function normalizeScoringSpecShape(raw) {
  if (raw == null || raw === "") return null;

  let parsed = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw.trim());
      if (typeof parsed === "string") {
        parsed = JSON.parse(parsed);
      }
    } catch {
      return null;
    }
  }

  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;

  if (
    parsed.weights &&
    typeof parsed.weights === "object" &&
    !Array.isArray(parsed.weights)
  ) {
    return parsed;
  }

  const entries = Object.entries(parsed);
  if (
    entries.length > 0 &&
    entries.every(([key, value]) => typeof key === "string" && Number.isFinite(Number(value)))
  ) {
    return { weights: parsed };
  }

  return null;
}

/** Parse scoring spec from nested object or `scoring_spec_json` column shapes. */
export function parseScoringSpecFromRecord(source) {
  if (!source || typeof source !== "object") return null;

  const candidates = [];
  const push = (value, priority) => {
    const normalized = normalizeScoringSpecShape(value);
    if (normalized && scoringSpecWeightKeyCount(normalized) > 0) {
      candidates.push({ spec: normalized, priority });
    }
  };

  // Prefer persisted JSON column shapes over derived `scoring_spec` mirrors.
  push(source.scoring_spec_json ?? source.scoringSpecJson, 3);
  push(source.scoring_spec ?? source.scoringSpec, 2);

  const strategies = source.strategies ?? source.Strategies;
  if (Array.isArray(strategies)) {
    for (const strategy of strategies) {
      push(strategy?.scoring_spec_json ?? strategy?.scoringSpecJson, 3);
      push(strategy?.scoring_spec ?? strategy?.scoringSpec, 1);
    }
  }

  if (!candidates.length) return null;

  candidates.sort((a, b) => {
    const countDiff = scoringSpecWeightKeyCount(b.spec) - scoringSpecWeightKeyCount(a.spec);
    if (countDiff !== 0) return countDiff;
    return b.priority - a.priority;
  });

  return candidates[0].spec;
}

/**
 * Build scoring-spec fields for carrier-selection config create/update.
 * Sends both `scoring_spec` (object) and `scoring_spec_json` (string) — the backend
 * persists whichever form it receives into `resource_selection_config.scoring_spec_json`.
 * Simulation/lane-paths uses the same shape but does not write to the DB.
 */
export function buildScoringSpecPayload(kpis, { isUpdate = false } = {}) {
  const spec = buildKpiScoringSpec(kpis);
  const weightCount = Object.keys(spec.weights || {}).length;
  if (isUpdate && weightCount === 0) {
    const empty = { weights: {} };
    const json = JSON.stringify(empty);
    return {
      scoring_spec: empty,
      scoringSpec: empty,
      scoring_spec_json: json,
      scoringSpecJson: json,
    };
  }
  const json = JSON.stringify(spec);
  return {
    scoring_spec: spec,
    scoringSpec: spec,
    scoring_spec_json: json,
    scoringSpecJson: json,
  };
}

export function scoringSpecHasWeights(payload = {}) {
  const spec = payload.scoring_spec ?? payload.scoringSpec;
  const weights = spec?.weights;
  return weights && typeof weights === "object" && Object.keys(weights).length > 0;
}


/** Reverse `scoring_spec.weights` (0-1) into editable KPI rows. Optional `catalog` supplies variant display names. */
export function kpisFromScoringSpec(spec, catalog = null) {
  if (!spec?.weights || typeof spec.weights !== "object") return null;
  const labelByKey = {};
  if (Array.isArray(catalog)) {
    for (const base of catalog) {
      for (const variant of base.selectionVariants || []) {
        const displayName = variant.displayName || variant.variantCode;
        if (variant.scoringWeightKey) {
          labelByKey[variant.scoringWeightKey] = displayName;
        }
        if (variant.carrierKpiName) {
          labelByKey[variant.carrierKpiName] = displayName;
        }
        if (variant.variantCode) {
          labelByKey[scoringSpecKpiKey(variant.variantCode)] = displayName;
        }
      }
    }
  }
  for (const row of KPI_LIBRARY) {
    labelByKey[scoringSpecKpiKey(row.name)] = row.name;
  }
  const out = [];
  for (const [key, w] of Object.entries(spec.weights)) {
    const frac = Number(w);
    if (!Number.isFinite(frac)) continue;
    const pct = Math.round(frac * 10000) / 100;
    const name = labelByKey[key] || key.replace(/_/g, " ");
    out.push({
      name,
      percent: Math.min(100, Math.max(0, pct)),
      scoringWeightKey: key,
    });
  }
  return out.length ? out : null;
}

function carrierConfigGeoFromForm(form) {
  const oh = form?.originGeoHierarchy;
  const dh = form?.destinationGeoHierarchy;
  const origin = {
    origin_city: null,
    origin_state_code: null,
    origin_state_name: null,
    origin_postal_code: null,
    origin_country_code: null,
    origin_country_name: null,
  };
  if (oh === "City-State") {
    origin.origin_city = form.originCity || null;
    origin.origin_state_code = form.originState || null;
    origin.origin_country_code = form.originCountry || null;
    origin.origin_country_name = form.originCountry || null;
  } else if (oh === "ZIP") {
    origin.origin_postal_code = form.originPostalCode || null;
    origin.origin_state_code = form.originState || null;
    origin.origin_country_code = form.originCountry || null;
    origin.origin_country_name = form.originCountry || null;
  } else if (oh === "Country") {
    origin.origin_country_code = form.originGeoValue || null;
    origin.origin_country_name = form.originGeoValue || null;
  }
  const dest = {
    destination_city: null,
    destination_state_code: null,
    destination_state_name: null,
    destination_postal_code: null,
    destination_country_code: null,
    destination_country_name: null,
  };
  if (dh === "City-State") {
    dest.destination_city = form.destCity || null;
    dest.destination_state_code = form.destState || null;
    dest.destination_country_code = form.destCountry || null;
    dest.destination_country_name = form.destCountry || null;
  } else if (dh === "ZIP") {
    dest.destination_postal_code = form.destPostalCode || null;
    dest.destination_state_code = form.destState || null;
    dest.destination_country_code = form.destCountry || null;
    dest.destination_country_name = form.destCountry || null;
  } else if (dh === "Country") {
    dest.destination_country_code = form.destinationGeoValue || null;
    dest.destination_country_name = form.destinationGeoValue || null;
  }
  return { ...origin, ...dest };
}

export function pickConfigurationName(obj) {
  if (obj == null) return "";
  if (typeof obj !== "object") return String(obj).trim();
  const direct =
    obj.configuration_name ??
    obj.configurationName ??
    obj.config_name ??
    obj.configName ??
    obj.name;
  if (direct != null && String(direct).trim()) return String(direct).trim();
  const nested = obj.carrier_selection_config ?? obj.carrierSelectionConfig ?? obj.configuration;
  if (nested && typeof nested === "object") return pickConfigurationName(nested);
  return "";
}

const GEO_UI_TO_API = {
  "Any Location": "ANY-LOCATION",
  Location: "LOCATION",
  "City-State": "CITY-STATE",
  ZIP: "ZIP",
  Country: "COUNTRY",
};

const GEO_API_TO_UI = Object.fromEntries(
  Object.entries(GEO_UI_TO_API).map(([ui, api]) => [api, ui])
);

/** Legacy API values no longer offered in the UI � hydrate as Any Location. */
const LEGACY_GEO_API_FALLBACK = {
  REGION: "Any Location",
  "REGION-CODE": "Any Location",
};

export const mapGeoHierarchyUiToApi = (label) =>
  GEO_UI_TO_API[label] || (label ? String(label).toUpperCase().replace(/\s+/g, "-") : null);

export const mapGeoHierarchyApiToUi = (apiVal) => {
  const key = apiVal != null ? String(apiVal).trim() : "";
  if (!key) return "Any Location";
  if (GEO_API_TO_UI[key]) return GEO_API_TO_UI[key];
  if (LEGACY_GEO_API_FALLBACK[key]) return LEGACY_GEO_API_FALLBACK[key];
  return apiVal || "Any Location";
};

const pickOriginGeoDisplay = (row) => {
  const hierarchy = (row.origin_geo_hierarchy ?? row.originGeoHierarchy ?? "")
    .toString()
    .trim();
  const hUpper = hierarchy.toUpperCase();
  if (hUpper.includes("LOCATION")) {
    return (
      row.origin_location_name ?? row.origin_location_code ?? hierarchy
    ) || "--";
  }
  if (hUpper.includes("COUNTRY")) {
    return (
      row.origin_country_name ?? row.origin_country_code ?? hierarchy
    ) || "--";
  }
  if (hUpper.includes("CITY")) {
    return (row.origin_city ?? hierarchy) || "--";
  }
  if (hUpper.includes("STATE")) {
    return (
      row.origin_state_name ?? row.origin_state_code ?? hierarchy
    ) || "--";
  }
  if (hUpper.includes("POSTAL") || hUpper.includes("ZIP")) {
    return (row.origin_postal_code ?? hierarchy) || "--";
  }
  return hierarchy || "--";
};

/** Table row for ACS list (matches existing column usage). */
export const formatParityConfigListItem = (item) => {
  const row = normalizeCarrierConfigRecord(item);
  const id = row.carrier_selection_config_id ?? row.carrierSelectionConfigId;
  const name = pickConfigurationName(row);
  const active = Boolean(row.is_active ?? row.isActive);
  const hazmatFlag = row.is_hazmat ?? row.isHazmat;
  return {
    id: id != null ? String(id) : "",
    name: name || `Config ${id ?? ""}`,
    status: active ? "Active" : "Inactive",
    mode: row.shipment_type ?? row.shipmentType ?? "--",
    type: row.transport_mode ?? row.transportMode ?? "--",
    hazmat: hazmatFlag ? "Yes" : "No",
    officeCode:
      row.originator_id ?? row.originatorId ?? row.originator ?? "--",
    originGeo: pickOriginGeoDisplay(row),
    originLocation: row.origin_geo_hierarchy ?? row.originGeoHierarchy ?? "--",
    destinationLocation: row.destination_geo_hierarchy ?? row.destinationGeoHierarchy ?? "--",
    active,
    _parity: true,
  };
};

const firstStrategy = (strategies) =>
  Array.isArray(strategies) && strategies.length ? strategies[0] : null;

const isTenderMetadata = (meta) => {
  if (!meta) return false;
  const t = (meta.strategy_type || meta.strategyType || "")
    .toString()
    .trim()
    .toLowerCase();
  return t === "tender";
};

export const buildParityStrategyRequests = (editableStrategies, strategyMetadataList) => {
  const list = Array.isArray(editableStrategies) ? editableStrategies : [];
  if (!list.length) {
    throw new Error("At least one strategy is required.");
  }
  return list.map((strategy, idx) => {
    const priority = idx + 1;
    const metaId =
      strategy.metadataId ||
      strategy.metadata_id ||
      resolveMetadataIdForBasis(strategy.basis, strategyMetadataList);
    if (!metaId) {
      throw new Error(
        "Strategy template is missing metadata_id. Reload the page or contact support."
      );
    }
    const meta = findMetadataById(metaId, strategyMetadataList);
    const tender = meta
      ? isTenderMetadata(meta)
      : Number(strategy.tenderCycles ?? 0) > 0 || Boolean(strategy.autoTender);
    const tenderCycles = tender ? Math.max(1, Number(strategy.tenderCycles ?? 3)) : null;
    const mustRespondValueRaw =
      strategy.mustRespondByValue ?? strategy.maxLeadTimeHrs;
    const mustRespondValue = Number(mustRespondValueRaw);
    if (
      mustRespondValueRaw === "" ||
      mustRespondValueRaw == null ||
      !Number.isFinite(mustRespondValue) ||
      mustRespondValue < 1
    ) {
      throw new Error(
        "Must Respond By Value is required for every strategy and must be at least 1."
      );
    }
    const mustRespondUnit = (strategy.mustRespondByUnit || "").toString().trim();
    if (!mustRespondUnit) {
      throw new Error(
        "Must Respond By Unit is required for every strategy."
      );
    }
    const allocationRows = buildAllocationWeightsPayload(strategy);
    return {
      metadata_id: typeof metaId === "string" ? metaId : String(metaId),
      priority,
      tender_cycles: tenderCycles,
      auto_tender: tender ? Boolean(strategy.autoTender ?? true) : false,
      auto_accept: Boolean(strategy.autoAccept ?? false),
      auto_recall: Boolean(strategy.autoRecall ?? false),
      honour_carrier_working_hours_for_auto_tender_and_recall: Boolean(
        strategy.honourCarrierWorkingHours ??
          strategy.honour_carrier_working_hours_for_auto_tender_and_recall ??
          false
      ),
      utilise_capacity_after_commitment:
        strategy.utiliseCapacityAfterCommitment !== false,
      include_rejected_carriers: Boolean(strategy.includeRejectedCarriers ?? false),
      must_respond_by_value: mustRespondValue,
      must_respond_by_unit: mustRespondUnit,
      min_start_time_value: Number(strategy.minLeadTimeHrs ?? strategy.minStartTimeValue ?? 2),
      min_start_time_unit: strategy.minStartTimeUnit || "hours",
      max_start_time_value: Number(strategy.maxLeadTimeHrs ?? strategy.maxStartTimeValue ?? 72),
      max_start_time_unit: strategy.maxStartTimeUnit || "hours",
      allocation_mode: PARITY_ALLOCATION_MODE,
      allocation_weights: allocationRows,
      allocationMode: PARITY_ALLOCATION_MODE,
      allocationWeights: allocationRows,
    };
  });
};

function metadataRowId(m) {
  if (!m || typeof m !== "object") return null;
  return (
    m.strategy_metadata_id ??
    m.strategyMetadataId ??
    m.strategy_type_metadata_id ??
    m.strategyTypeMetadataId ??
    m.metadata_id ??
    m.metadataId ??
    m.id ??
    null
  );
}

function findMetadataById(id, strategyMetadataList) {
  const sid = String(id);
  return (strategyMetadataList || []).find((m) => String(metadataRowId(m)) === sid);
}

export function resolveMetadataIdForBasis(basis, strategyMetadataList) {
  const b = (basis || "").toString().trim().toUpperCase();
  const list = strategyMetadataList || [];
  const order =
    b === "PRICE" || b === "COST"
      ? [b, "RANK", "COST", "PRICE"]
      : b === "RANK"
        ? ["RANK", "PRICE", "COST"]
        : [b, "RANK", "COST"];
  const seen = new Set();
  for (const c of order) {
    if (seen.has(c)) continue;
    seen.add(c);
    const byBasis = list.find(
      (m) => (m.strategy_assignment_basis || m.strategyAssignmentBasis || "").toUpperCase() === c
    );
    if (byBasis) return metadataRowId(byBasis);
  }
  return metadataRowId(list[0]);
}

export function attachMetadataToTemplate(template, strategyMetadataList) {
  const metadataId = resolveMetadataIdForBasis(template.basis, strategyMetadataList);
  const meta = findMetadataById(metadataId, strategyMetadataList);
  return {
    ...template,
    metadataId,
    _parityMeta: meta || null,
  };
}

/** Each strategy can be saved without the metadata catalogue (ids came from API or prior attach). */
export function strategiesHaveResolvedMetadataIds(strategies) {
  const list = Array.isArray(strategies) ? strategies : [];
  if (!list.length) return false;
  return list.every((s) => {
    const id = s.metadataId ?? s.metadata_id;
    return id != null && String(id).trim() !== "";
  });
}

export const buildParityCarrierConfigRequest = ({
  form,
  editableStrategies,
  officeCode,
  strategyMetadataList,
  isUpdate,
}) => {
  const name = (pickConfigurationName(form) || (form.configName || "")).trim();
  const oc = officeCode || form.originatorId || "";
  const strategies = buildParityStrategyRequests(editableStrategies, strategyMetadataList);
  const geo = carrierConfigGeoFromForm(form);
  const rootAllocation = buildAllocationWeightsPayload(editableStrategies?.[0]);
  const scoringPayload = buildScoringSpecPayload(editableStrategies?.[0]?.kpis, { isUpdate });
  const body = {
    originator: oc,
    originator_id: form.originatorId || oc,
    configuration_name: name,
    effective_date: form.effectiveDate || null,
    expiry_date: form.expiryDate || null,
    origin_location_name: null,
    origin_location_code: null,
    origin_geo_hierarchy: mapGeoHierarchyUiToApi(form.originGeoHierarchy),
    origin_address1: null,
    origin_address2: null,
    origin_uuid: form.originLocationId || null,
    destination_location_name: null,
    destination_location_code: null,
    destination_geo_hierarchy: mapGeoHierarchyUiToApi(form.destinationGeoHierarchy),
    destination_address1: null,
    destination_address2: null,
    destination_uuid: form.destLocationId || null,
    ...geo,
    Isrefer: Boolean(form.isRefer),
    is_hazmat: Boolean(form.hazmat),
    auto_select_carrier: Boolean(form.autoSelectCarrier ?? true),
    transport_mode: form.transportMode || null,
    shipment_type: form.shipmentType || null,
    ...scoringPayload,
    allocation_mode: PARITY_ALLOCATION_MODE,
    allocation_weights: rootAllocation,
    allocationMode: PARITY_ALLOCATION_MODE,
    allocationWeights: rootAllocation,
    strategies,
  };
  if (isUpdate) {
    body.is_active = Boolean(form.active);
  }
  return body;
};

export const parityConfigResponseToEditForm = (raw, catalog = null) => {
  const data = normalizeCarrierConfigRecord(raw);
  const id = data.carrier_selection_config_id ?? data.carrierSelectionConfigId;
  const strategiesRaw = data.strategies || [];
  const displayName = pickConfigurationName(data);
  const scoringSpec = parseScoringSpecFromRecord(data);
  let strategies = strategiesRaw.map(parityStrategyToEditable);
  const rootAlloc = rawAllocationWeightsArrayFrom(data);
  if (strategies.length && Array.isArray(rootAlloc) && rootAlloc.length) {
    const sr0 = strategiesRaw[0];
    const sr0Raw = sr0 ? rawAllocationWeightsArrayFrom(sr0) : null;
    if (!Array.isArray(sr0Raw) || sr0Raw.length === 0) {
      const merged = allocationWeightsFromStrategyResponse({
        allocation_weights: rootAlloc,
      });
      strategies = strategies.map((s, i) => (i === 0 ? { ...s, allocationWeights: merged } : s));
    }
  }
  const specKpis = kpisFromScoringSpec(scoringSpec, catalog);
  if (specKpis?.length && strategies.length) {
    strategies = strategies.map((s, i) => (i === 0 ? { ...s, kpis: specKpis } : s));
  }
  const originH = mapGeoHierarchyApiToUi(data.origin_geo_hierarchy ?? data.originGeoHierarchy);
  const destH = mapGeoHierarchyApiToUi(
    data.destination_geo_hierarchy ?? data.destinationGeoHierarchy
  );

  return {
    editConfig: {
      id: id != null ? String(id) : "",
      configName: displayName,
      originatorId: data.originator_id ?? data.originatorId ?? "",
      active: Boolean(data.is_active ?? data.isActive),
      effectiveDate: data.effective_date ?? data.effectiveDate ?? "",
      expiryDate: data.expiry_date ?? data.expiryDate ?? "",
      shipmentType: data.shipment_type ?? data.shipmentType ?? "FTL",
      transportMode: data.transport_mode ?? data.transportMode ?? "ROAD",
      hazmat: Boolean(data.is_hazmat ?? data.isHazmat),
      autoSelectCarrier: Boolean(data.auto_select_carrier ?? data.autoSelectCarrier ?? true),
      originGeoHierarchy: originH,
      destinationGeoHierarchy: destH,
      paymentMethod: "Prepaid",
      originGeoValue:
        originH === "Country"
          ? data.origin_country_name || data.origin_country_code || ""
          : originH === "Location"
            ? data.origin_location_name || ""
            : "",
      destinationGeoValue:
        destH === "Country"
          ? data.destination_country_name || data.destination_country_code || ""
          : destH === "Location"
            ? data.destination_location_name || ""
            : "",
      originCity: data.origin_city ?? "",
      originState: data.origin_state_code ?? data.origin_state_name ?? "",
      originCountry: data.origin_country_name ?? data.origin_country_code ?? "",
      originPostalCode: data.origin_postal_code ?? "",
      destCity: data.destination_city ?? "",
      destState: data.destination_state_code ?? data.destination_state_name ?? "",
      destCountry: data.destination_country_name ?? data.destination_country_code ?? "",
      destPostalCode: data.destination_postal_code ?? "",
      originLocationId: data.origin_uuid ?? data.originUuid ?? "",
      destLocationId: data.destination_uuid ?? data.destinationUuid ?? "",
      isRefer: Boolean(data.Isrefer ?? data.is_refer ?? data.isRefer ?? false),
    },
    strategies,
    selectedConfig: {
      id: id != null ? String(id) : "",
      name: displayName,
      status: (data.is_active ?? data.isActive) ? "Active" : "Inactive",
      mode: data.shipment_type ?? data.shipmentType,
      type: data.transport_mode ?? data.transportMode,
      hazmat: (data.is_hazmat ?? data.isHazmat) ? "Yes" : "No",
      officeCode:
        data.originator_id ?? data.originatorId ?? data.originator ?? "--",
      originGeo: pickOriginGeoDisplay(data),
      originLocation: data.origin_geo_hierarchy ?? data.originGeoHierarchy,
      destinationLocation:
        data.destination_geo_hierarchy ?? data.destinationGeoHierarchy,
      active: Boolean(data.is_active ?? data.isActive),
      _parity: true,
    },
  };
};

export const parityStrategyToEditable = (sr, idx = 0) => {
  const meta = sr.metadata ?? sr.strategyMetadata;
  const basis = (meta?.strategy_assignment_basis || meta?.strategyAssignmentBasis || "RANK")
    .toString()
    .trim()
    .toUpperCase();
  const sid =
    sr.carrier_selection_strategy_id ??
    sr.carrierSelectionStrategyId ??
    `parity-${idx}`;
  const mid = sr.metadata_id ?? sr.metadataId;
  return {
    id: String(sid),
    metadataId: mid != null ? String(mid) : null,
    key: STRATEGY_LIBRARY[0].key,
    title: STRATEGY_LIBRARY[0].title,
    subtitle: STRATEGY_LIBRARY[0].subtitle,
    basis,
    /** Ranked parity always uses weighted allocation in UI � coerce legacy STRICT reads. */
    allocationMode: PARITY_ALLOCATION_MODE,
    minLeadTimeHrs: sr.min_start_time_value ?? sr.minStartTimeValue ?? 2,
    maxLeadTimeHrs: sr.max_start_time_value ?? sr.maxStartTimeValue ?? 72,
    tenderCycles: sr.tender_cycles ?? sr.tenderCycles ?? 3,
    autoTender: sr.auto_tender ?? sr.autoTender ?? false,
    autoAccept: sr.auto_accept ?? sr.autoAccept ?? false,
    autoRecall: sr.auto_recall ?? sr.autoRecall ?? false,
    honourCarrierWorkingHours: Boolean(
      sr.honour_carrier_working_hours_for_auto_tender_and_recall ??
        sr.honourCarrierWorkingHoursForAutoTenderAndRecall ??
        sr.honourCarrierWorkingHours ??
        false
    ),
    utiliseCapacityAfterCommitment:
      sr.utilise_capacity_after_commitment ?? sr.utiliseCapacityAfterCommitment !== false,
    includeRejectedCarriers: sr.include_rejected_carriers ?? sr.includeRejectedCarriers ?? false,
    mustRespondByValue:
      sr.must_respond_by_value ??
      sr.mustRespondByValue ??
      sr.max_start_time_value ??
      sr.maxStartTimeValue ??
      72,
    mustRespondByUnit:
      sr.must_respond_by_unit ??
      sr.mustRespondByUnit ??
      sr.max_start_time_unit ??
      sr.maxStartTimeUnit ??
      "hours",
    kpis: [],
    allocationWeights: allocationWeightsFromStrategyResponse(sr),
    resetPeriod: "Monthly",
    overflowBehavior: "Cascade to next ranked carrier",
  };
};

export const isPersistedParityStrategyId = (id) => {
  if (id == null || id === "") return false;
  const s = String(id);
  if (s.startsWith("draft-") || s.startsWith("parity-")) return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
};
